import { useState } from 'react';

import { ECountry } from 'App/i18n/i18n.types';
import { useTranslation } from 'react-i18next';

import { useToast } from '@nimbus-ds/components';

import { addCredit } from 'commons/utils/query/add-credit';

import { AMP_BUTTON, sendAmplitudeData } from '../amplitude';
import {
  BREAKPOINTS,
  DISPATCH_ON_SUCCESS_COUNTRIES,
  TOAST_DURATION_IN_MS
} from '../constants';
import { useBalance } from '../query/hooks';
import { storeDomain } from '../services';

import { useStoreContext } from './useStoreContext';

const useAddCredit = () => {
  const { refetch } = useBalance();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { country } = useStoreContext();

  const [isLoading, setIsLoading] = useState(false);

  const handleAddingCredit = async (
    value: number,
    orderIds?: string[]
  ): Promise<string | undefined> => {
    try {
      setIsLoading(true);

      const response = await addCredit(value);
      const url = response.checkoutUrl;

      const isMobileDevice = window.innerWidth <= BREAKPOINTS.md;

      if (url.includes('/admin/account')) {
        const urlBuilt = DISPATCH_ON_SUCCESS_COUNTRIES.includes(country)
          ? await buildDispatchOnSuccessUrl(url, orderIds)
          : await buildUrl(url);

        sendAmplitudeData(AMP_BUTTON, {
          action: 'Billing - Top up - Go to payment'
        });

        return urlBuilt;
      } else {
        if (!isMobileDevice) {
          window.open(url);
        }

        refetch();
        return url;
      }
    } catch (error) {
      addToast({
        id: 'error-add-credit',
        type: 'danger',
        text: t('widgets.add-credit.texts.error-payment'),
        duration: TOAST_DURATION_IN_MS
      });
    } finally {
      setIsLoading(false);
    }
  };

  const buildUrl = async (url: string) => {
    const store = await storeDomain();

    return store.domain + url;
  };

  const buildDispatchOnSuccessUrl = async (
    url: string,
    orderIds?: string[]
  ) => {
    const { domain } = await storeDomain();
    const baseUrl = domain + url;

    if (!orderIds) {
      return baseUrl;
    }

    const urlObj = new URL(baseUrl);

    const appName = country === ECountry.BRAZIL ? 'nuvemenvio' : 'envionube';
    const subPath = '/checkout/success?orderIds=' + orderIds.join(',');
    const callbackUrl = `${domain}/admin/apps/${appName}/${country.toLowerCase()}#${subPath}`;

    urlObj.searchParams.set('callbackUrl', encodeURIComponent(callbackUrl));

    return urlObj.toString();
  };

  const handleParseUrl = (url: string): string => {
    const adminPrefix = '/admin';
    const adminIndex = url.indexOf(adminPrefix);

    if (adminIndex === -1) {
      return url;
    }

    const path = url.slice(adminIndex + adminPrefix.length) || '/';

    return path;
  };

  return { handleAddingCredit, isLoading, handleParseUrl };
};

export default useAddCredit;
